import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface GraphQLRequest {
  query: string;
  variables?: any;
  operationName?: string;
}

export interface GraphQLResponse<T = any> {
  data?: T;
  errors?: Array<{
    message: string;
    locations?: Array<{ line: number; column: number }>;
    path?: Array<string | number>;
  }>;
  extensions?: any;
}

@Injectable({
  providedIn: 'root',
})
export class GraphQLService {
  private vendureToken: string | null = null;
  private adminToken: string | null = null;
  private sessionToken: string | null = null;

  constructor(private http: HttpClient) {
    // Try to load tokens from localStorage on initialization
    this.vendureToken = localStorage.getItem('vendure_token');
    this.adminToken = localStorage.getItem('adminToken');
    this.sessionToken = localStorage.getItem('vendure-session-token');
  }

  /**
   * Login method - store session token from headers/cookies
   */
  login(username: string, password: string): Observable<any> {
    const loginQuery = `
      mutation {
        login(username: "${username}", password: "${password}") {
          ... on CurrentUser {
            id
            identifier
            channels {
              id
              code
            }
          }
          ... on InvalidCredentialsError {
            errorCode
            message
          }
        }
      }
    `;

    return this.http
      .post<any>(
        `${environment.vendureAPI}`,
        {
          query: loginQuery,
        },
        {
          withCredentials: true, // CRUCIAL: Include cookies
          observe: 'response', // Get full response including headers
        }
      )
      .pipe(
        tap((response: any) => {
          // Store session token from headers or cookies
          const sessionToken =
            response.headers?.get('vendure-auth-token') ||
            response.headers?.get('Vendure-Auth-Token') ||
            this.getCookie('vendure-auth-token');

          if (sessionToken) {
            this.sessionToken = sessionToken;
            localStorage.setItem('vendure-session-token', sessionToken);
            // console.log('✅ Session token stored:', sessionToken);
          } else {
            // console.log('⚠️ No session token found in headers, using cookies');
          }
        }),
        map((response: any) => response.body) // Return just the body
      );
  }

  /**
   * Get session token from localStorage or cookies
   */
  getSessionToken(): string | null {
    // Try localStorage first
    let token =
      this.sessionToken || localStorage.getItem('vendure-session-token');

    // If not in localStorage, try cookies
    if (!token) {
      token = this.getCookie('vendure-auth-token');
    }

    return token;
  }

  /**
   * Helper method to get cookies
   */
  getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
    return null;
  }

  /**
   * Set admin token for Bearer authentication
   */
  setAdminToken(token: string) {
    this.adminToken = token;
    localStorage.setItem('adminToken', token);
    // console.log('✅ Admin token stored:', token);
  }

  /**
   * Get admin token from memory or localStorage
   */
  getAdminToken(): string | null {
    if (!this.adminToken) {
      this.adminToken = localStorage.getItem('adminToken');
    }
    return this.adminToken;
  }

  /**
   * Clear admin token from memory and localStorage
   */
  clearAdminToken(): void {
    this.adminToken = null;
    localStorage.removeItem('adminToken');
    // console.log('🗑️ Admin token cleared');
  }

  /**
   * Execute admin query with Bearer token authentication
   */
  adminQuery<T = any>(query: string, variables?: any): Observable<T> {
    const sessionToken = this.getSessionToken();

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      ...(sessionToken && { Authorization: `Bearer ${sessionToken}` }),
    });

    // console.log('🔑 Making admin request with token:', sessionToken ? 'Present' : 'Missing');

    return this.http
      .post<GraphQLResponse<T>>(
        environment.vendureAPI,
        {
          query: query,
          variables: variables,
        },
        {
          headers,
          withCredentials: true, // CRUCIAL: Include cookies
        }
      )
      .pipe(
        map((response) => {
          if (response.errors && response.errors.length > 0) {
            throw new Error(response.errors[0].message);
          }
          return response.data as T;
        }),
        catchError((error) => {
          // console.error('GraphQL Admin Error:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Execute a GraphQL query/mutation with token or cookie-based authentication
   */
  request<T = any>(
    query: string,
    variables?: any,
    operationName?: string,
    forceUseCookies: boolean = false
  ): Observable<T> {
    const graphqlRequest: GraphQLRequest = {
      query,
      variables: variables || {},
      operationName,
    };

    // Build headers - include token if available
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const sessionToken = this.getSessionToken();
    if (sessionToken && !forceUseCookies) {
      headers = headers.set('Authorization', `Bearer ${sessionToken}`);
      // console.log('🔑 Using session token for request');
      // console.log('   - Token header: Authorization');
      // console.log('   - Token preview:', `${sessionToken.substring(0, 20)}...`);
      // console.log('   - Token length:', sessionToken.length, 'characters');
      // console.log('   - Request to:', environment.vendureAPI);
    } else {
      // console.log('🍪 Using cookies for request (no token available)');
    }

    return this.http
      .post<GraphQLResponse<T>>(environment.vendureAPI, graphqlRequest, {
        headers,
        withCredentials: true, // CRUCIAL: Always include cookies
      })
      .pipe(
        map((response) => {
          if (response.errors && response.errors.length > 0) {
            throw new Error(response.errors[0].message);
          }
          return response.data as T;
        }),
        catchError((error) => {
          console.error('GraphQL Error:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Execute multiple GraphQL operations in batch
   */
  batchRequest<T = any>(requests: GraphQLRequest[]): Observable<T[]> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const sessionToken = this.getSessionToken();
    if (sessionToken) {
      headers = headers.set('Authorization', `Bearer ${sessionToken}`);
    }

    return this.http
      .post<GraphQLResponse<T>[]>(environment.vendureAPI, requests, {
        headers,
        withCredentials: true,
      })
      .pipe(
        map((responses) =>
          responses.map((response) => {
            if (response.errors && response.errors.length > 0) {
              throw new Error(response.errors[0].message);
            }
            return response.data as T;
          })
        ),
        catchError((error) => {
          console.error('GraphQL Batch Error:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Execute query with automatic retry on authentication failure
   */
  requestWithAuth<T = any>(
    query: string,
    variables?: any,
    operationName?: string
  ): Observable<T> {
    return this.request<T>(query, variables, operationName).pipe(
      catchError((error) => {
        if (error.status === 401) {
          // Handle authentication error
          // console.log('Authentication failed, redirecting to login...');
          // You can implement auto-retry logic here if needed
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Clear session on logout
   */
  logout(): void {
    this.sessionToken = null;
    this.adminToken = null;
    this.vendureToken = null;
    localStorage.removeItem('vendure-session-token');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('vendure_token');

    // Clear cookies by setting expired date
    document.cookie =
      'vendure-auth-token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie =
      'vendure-auth-token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.kortobaa.net;';

    // console.log('🗑️ All tokens cleared');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!(this.getSessionToken() || this.getCookie('vendure-auth-token'));
  }

  /**
   * Token Management Methods
   */

  /**
   * Set the Vendure authentication token
   */
  setVendureToken(token: string): void {
    // console.log('💾 Setting Vendure token...');
    // console.log('   - Token length:', token.length, 'characters');
    // console.log('   - Token preview:', `${token.substring(0, 30)}...`);

    this.vendureToken = token;
    localStorage.setItem('vendure_token', token);

    // Verify it was saved
    const saved = localStorage.getItem('vendure_token');
    // console.log('✅ Vendure token saved successfully');
    // console.log('   - Saved to memory:', !!this.vendureToken);
    // console.log('   - Saved to localStorage:', !!saved);
    // console.log('   - Token matches:', saved === token);
  }

  /**
   * Get the current Vendure authentication token
   */
  getVendureToken(): string | null {
    return this.vendureToken;
  }

  /**
   * Clear the Vendure authentication token
   */
  clearVendureToken(): void {
    // console.log('🗑️ Clearing Vendure token...');
    // console.log('   - Had token:', !!this.vendureToken);

    this.vendureToken = null;
    localStorage.removeItem('vendure_token');

    // Verify it was cleared
    const cleared = !localStorage.getItem('vendure_token');
    // console.log('✅ Vendure token cleared successfully');
    // console.log('   - Memory cleared:', !this.vendureToken);
    // console.log('   - localStorage cleared:', cleared);
  }

  /**
   * Check if we have a valid token
   */
  hasVendureToken(): boolean {
    return !!this.vendureToken;
  }
}
